import { writable } from "svelte/store";
import type { Writable } from "svelte/store";
import { DirStatsCache } from "./dir-stats";
import * as filepath from "./filepath";
import { FsDir, kInvalidEntry, type FsEntry } from "./fsentry";
import { xhrGetAsBlob, type HttpProgress } from "./http";
import { fmtSize, getObjectValue, len, nanoid, startTimer, throwIf, trimSuffix } from "./util";

export type FSType =
  | "local:"
  | "s3:"
  | "dropbox:"
  | "onedrive:"
  | "gdrive:"
  | "backblaze:"
  | "putio:"
  | "pixeldrain:"
  | "storj:"
  | "sftp:"
  | "ftp:"
  | "smb:"
  | "webdav:"
  | "box:"
  | "azureblob:"
  | "gcs:"
  | "pcloud:"
  | "mega:"
  | "koofr:"
  | "seafile:"
  | "yandex:"
  | "jottacloud:"
  | "swift:"
  | "fichier:"
  | "opendrive:"
  | "gofile:"
  | "premiumize:"
  | "internetarchive:"
  | "azurefiles:"
  | "http:"
  | "filescom:"
  | "filelu:"
  | "imagekit:"
  | "cloudinary:"
  | "drime:"
  | "quatrix:"
  | "linkbox:"
  | "ulozto:"
  | "testfiles:";

export const fsTypeS3: FSType = "s3:";
export const fsTypeBackBlaze: FSType = "backblaze:";
export const fsTypeDropbox: FSType = "dropbox:";
export const fsTypeOneDrive: FSType = "onedrive:";
export const fsTypeGDrive: FSType = "gdrive:";
export const fsTypePutio: FSType = "putio:";
export const fsTypePixeldrain: FSType = "pixeldrain:";
export const fsTypeStorj: FSType = "storj:";
export const fsTypeSftp: FSType = "sftp:";
export const fsTypeFtp: FSType = "ftp:";
export const fsTypeSmb: FSType = "smb:";
export const fsTypeWebdav: FSType = "webdav:";
export const fsTypeBox: FSType = "box:";
export const fsTypeAzureBlob: FSType = "azureblob:";
export const fsTypeGcs: FSType = "gcs:";
export const fsTypePcloud: FSType = "pcloud:";
export const fsTypeMega: FSType = "mega:";
export const fsTypeKoofr: FSType = "koofr:";
export const fsTypeSeafile: FSType = "seafile:";
export const fsTypeYandex: FSType = "yandex:";
export const fsTypeJottacloud: FSType = "jottacloud:";
export const fsTypeSwift: FSType = "swift:";
export const fsTypeFichier: FSType = "fichier:";
export const fsTypeOpendrive: FSType = "opendrive:";
export const fsTypeGofile: FSType = "gofile:";
export const fsTypePremiumize: FSType = "premiumize:";
export const fsTypeInternetArchive: FSType = "internetarchive:";
export const fsTypeAzureFiles: FSType = "azurefiles:";
export const fsTypeHttp: FSType = "http:";
export const fsTypeFilesCom: FSType = "filescom:";
export const fsTypeFilelu: FSType = "filelu:";
export const fsTypeImagekit: FSType = "imagekit:";
export const fsTypeCloudinary: FSType = "cloudinary:";
export const fsTypeDrime: FSType = "drime:";
export const fsTypeQuatrix: FSType = "quatrix:";
export const fsTypeLinkbox: FSType = "linkbox:";
export const fsTypeUlozto: FSType = "ulozto:";
export const fsTypeLocal: FSType = "local:";
export const fsTypeTestFiles: FSType = "testfiles:";

let allFsTypes: FSType[] = [
  fsTypeS3,
  fsTypeDropbox,
  fsTypeOneDrive,
  fsTypeGDrive,
  fsTypeBackBlaze,
  fsTypePutio,
  fsTypePixeldrain,
  fsTypeStorj,
  fsTypeSftp,
  fsTypeFtp,
  fsTypeSmb,
  fsTypeWebdav,
  fsTypeBox,
  fsTypeAzureBlob,
  fsTypeGcs,
  fsTypePcloud,
  fsTypeMega,
  fsTypeKoofr,
  fsTypeSeafile,
  fsTypeYandex,
  fsTypeJottacloud,
  fsTypeSwift,
  fsTypeFichier,
  fsTypeOpendrive,
  fsTypeGofile,
  fsTypePremiumize,
  fsTypeInternetArchive,
  fsTypeAzureFiles,
  fsTypeHttp,
  fsTypeFilesCom,
  fsTypeFilelu,
  fsTypeImagekit,
  fsTypeCloudinary,
  fsTypeDrime,
  fsTypeQuatrix,
  fsTypeLinkbox,
  fsTypeUlozto,
  fsTypeLocal,
  fsTypeTestFiles,
];

function newFsID(prefix: string): string {
  return prefix + nanoid(8);
}

export function fsTypeFromID(fsID: string): FSType {
  for (let type of allFsTypes) {
    if (fsID.startsWith(type)) {
      return type;
    }
  }
  throw new Error(`Unknown fsID: ${fsID}`);
}

// Base config for all filesystems
export interface FSConfigBase {
  // unique id of this config
  fsID: string;
  name: string;
}

// S3 configuration
export interface S3Config extends FSConfigBase {
  access: string;
  secret: string;
  bucket: string;
  endpoint: string;
}

export function fsTypeFromFSConfig(fsConfig: FSConfigBase): FSType {
  return fsTypeFromID(fsConfig.fsID);
}

export function createS3Config(): S3Config {
  return {
    fsID: newFsID(fsTypeS3),
    name: "",

    access: "",
    secret: "",
    bucket: "",
    endpoint: "",
  };
}

function s3ConfigEq(c1: S3Config, c2: S3Config): boolean {
  return c1.access == c2.access && c1.secret == c2.secret && c1.bucket == c2.bucket && c1.endpoint == c2.endpoint;
}

export interface DropboxConfig extends FSConfigBase {
  accessToken: string;
  refreshToken: string;
  email: string;
  name: string;
}

export function createDropboxConfig(): DropboxConfig {
  return {
    fsID: fsTypeDropbox, // only one dropbox connection
    name: "DropBox",
    accessToken: "",
    refreshToken: "",
    email: "",
  };
}

export interface OneDriveConfig extends FSConfigBase {
  accessToken: string;
  refreshToken: string;
  tokenExpires: string; // Date as string
  email: string;
}

export function createOneDriveConfig(): OneDriveConfig {
  return {
    fsID: fsTypeOneDrive, // only one onedrive connection
    name: "OneDrive",
    accessToken: "",
    refreshToken: "",
    tokenExpires: "",
    email: "",
  };
}

export interface GDriveConfig extends FSConfigBase {
  accessToken: string;
  tokenExpires: string; // Date as string
  email: string;
}

export function createGDriveConfig(): GDriveConfig {
  return {
    fsID: fsTypeGDrive, // only one Google Drive connection
    name: "",
    accessToken: "",
    tokenExpires: "",
    email: "",
  };
}

export interface PutioConfig extends FSConfigBase {
  accessToken: string;
  username: string;
}

export function createPutioConfig(): PutioConfig {
  return {
    fsID: newFsID(fsTypePutio),
    name: "",
    accessToken: "",
    username: "",
  };
}

export function putioConfigEq(c1: PutioConfig, c2: PutioConfig): boolean {
  return c1.accessToken == c2.accessToken;
}

export interface PixeldrainConfig extends FSConfigBase {
  apiKey: string;
  username: string;
}

export function createPixeldrainConfig(): PixeldrainConfig {
  return {
    fsID: newFsID(fsTypePixeldrain),
    name: "",
    apiKey: "",
    username: "",
  };
}

export function pixeldrainConfigEq(c1: PixeldrainConfig, c2: PixeldrainConfig): boolean {
  return c1.apiKey == c2.apiKey;
}

export interface StorjConfig extends FSConfigBase {
  access: string;
  secret: string;
  bucket: string;
  endpoint: string;
}

export const storjGateway = "gateway.storjshare.io";

export function createStorjConfig(): StorjConfig {
  return {
    fsID: newFsID(fsTypeStorj),
    name: "",
    access: "",
    secret: "",
    bucket: "",
    endpoint: storjGateway,
  };
}

export function storjConfigEq(c1: StorjConfig, c2: StorjConfig): boolean {
  return c1.access == c2.access && c1.secret == c2.secret && c1.bucket == c2.bucket && c1.endpoint == c2.endpoint;
}

export interface SftpConfig extends FSConfigBase {
  host: string;
  port: string;
  user: string;
  password: string;
  key: string;
  keyPass: string;
  hostKey: string;
}

export function createSftpConfig(): SftpConfig {
  return {
    fsID: newFsID(fsTypeSftp),
    name: "",
    host: "",
    port: "22",
    user: "",
    password: "",
    key: "",
    keyPass: "",
    hostKey: "",
  };
}

export function sftpConfigEq(c1: SftpConfig, c2: SftpConfig): boolean {
  return (
    c1.host == c2.host &&
    c1.port == c2.port &&
    c1.user == c2.user &&
    c1.password == c2.password &&
    c1.key == c2.key &&
    c1.keyPass == c2.keyPass &&
    c1.hostKey == c2.hostKey
  );
}

export interface SmbConfig extends FSConfigBase {
  host: string;
  // empty means 445
  port: string;
  // empty means the guest account
  user: string;
  password: string;
  domain: string;
  // empty means all shares of the server: "/" lists them
  share: string;
}

export function createSmbConfig(): SmbConfig {
  return {
    fsID: newFsID(fsTypeSmb),
    name: "",
    host: "",
    port: "",
    user: "",
    password: "",
    domain: "",
    share: "",
  };
}

export function smbConfigEq(c1: SmbConfig, c2: SmbConfig): boolean {
  return (
    c1.host == c2.host &&
    c1.port == c2.port &&
    c1.user == c2.user &&
    c1.password == c2.password &&
    c1.domain == c2.domain &&
    c1.share == c2.share
  );
}

export type FtpTLS = "none" | "explicit" | "implicit";

export interface FtpConfig extends FSConfigBase {
  host: string;
  // empty means the default for the TLS mode: 21, or 990 for implicit
  port: string;
  // empty means anonymous
  user: string;
  password: string;
  tls: FtpTLS;
}

export function createFtpConfig(): FtpConfig {
  return {
    fsID: newFsID(fsTypeFtp),
    name: "",
    host: "",
    port: "",
    user: "",
    password: "",
    tls: "none",
  };
}

export function ftpConfigEq(c1: FtpConfig, c2: FtpConfig): boolean {
  return (
    c1.host == c2.host && c1.port == c2.port && c1.user == c2.user && c1.password == c2.password && c1.tls == c2.tls
  );
}

export interface WebdavConfig extends FSConfigBase {
  url: string;
  user: string;
  password: string;
}

export function createWebdavConfig(): WebdavConfig {
  return {
    fsID: newFsID(fsTypeWebdav),
    name: "",
    url: "",
    user: "",
    password: "",
  };
}

export function webdavConfigEq(c1: WebdavConfig, c2: WebdavConfig): boolean {
  return c1.url == c2.url && c1.user == c2.user && c1.password == c2.password;
}

export interface BoxConfig extends FSConfigBase {
  accessToken: string;
  login: string;
}

export function createBoxConfig(): BoxConfig {
  return {
    fsID: newFsID(fsTypeBox),
    name: "",
    accessToken: "",
    login: "",
  };
}

export function boxConfigEq(c1: BoxConfig, c2: BoxConfig): boolean {
  return c1.accessToken == c2.accessToken;
}

export interface AzureBlobConfig extends FSConfigBase {
  account: string;
  key: string;
  container: string;
}

export function createAzureBlobConfig(): AzureBlobConfig {
  return {
    fsID: newFsID(fsTypeAzureBlob),
    name: "",
    account: "",
    key: "",
    container: "",
  };
}

export function azureBlobConfigEq(c1: AzureBlobConfig, c2: AzureBlobConfig): boolean {
  return c1.account == c2.account && c1.key == c2.key && c1.container == c2.container;
}

export interface GcsConfig extends FSConfigBase {
  // contents of the service account key file (JSON)
  key: string;
  bucket: string;
  // service account email, filled in after the first connect
  email: string;
}

export function createGcsConfig(): GcsConfig {
  return {
    fsID: newFsID(fsTypeGcs),
    name: "",
    key: "",
    bucket: "",
    email: "",
  };
}

export function gcsConfigEq(c1: GcsConfig, c2: GcsConfig): boolean {
  return c1.key == c2.key && c1.bucket == c2.bucket;
}

export interface PcloudConfig extends FSConfigBase {
  // login token: the password is not kept
  auth: string;
  // API host of the account's region
  host: string;
  email: string;
}

export function createPcloudConfig(): PcloudConfig {
  return {
    fsID: newFsID(fsTypePcloud),
    name: "",
    auth: "",
    host: "",
    email: "",
  };
}

export function pcloudConfigEq(c1: PcloudConfig, c2: PcloudConfig): boolean {
  return c1.email == c2.email && c1.host == c2.host;
}

export interface MegaConfig extends FSConfigBase {
  // session id from the login: the password is not kept
  sid: string;
  // the account's master key, base64url. It decrypts names and files.
  key: string;
  email: string;
}

export function createMegaConfig(): MegaConfig {
  return {
    fsID: newFsID(fsTypeMega),
    name: "",
    sid: "",
    key: "",
    email: "",
  };
}

export function megaConfigEq(c1: MegaConfig, c2: MegaConfig): boolean {
  return c1.email == c2.email;
}

export interface KoofrConfig extends FSConfigBase {
  email: string;
  // an app password, not the account password
  password: string;
  // id of the account's own storage
  mountId: string;
}

export function createKoofrConfig(): KoofrConfig {
  return {
    fsID: newFsID(fsTypeKoofr),
    name: "",
    email: "",
    password: "",
    mountId: "",
  };
}

export function koofrConfigEq(c1: KoofrConfig, c2: KoofrConfig): boolean {
  return c1.email == c2.email && c1.mountId == c2.mountId;
}

export interface SeafileConfig extends FSConfigBase {
  // address of the Seafile server
  url: string;
  user: string;
  // API token from the login: the password is not kept
  token: string;
}

export function createSeafileConfig(): SeafileConfig {
  return {
    fsID: newFsID(fsTypeSeafile),
    name: "",
    url: "",
    user: "",
    token: "",
  };
}

export function seafileConfigEq(c1: SeafileConfig, c2: SeafileConfig): boolean {
  return c1.url == c2.url && c1.user == c2.user;
}

export interface YandexConfig extends FSConfigBase {
  // OAuth token
  token: string;
  login: string;
}

export function createYandexConfig(): YandexConfig {
  return {
    fsID: newFsID(fsTypeYandex),
    name: "",
    token: "",
    login: "",
  };
}

export function yandexConfigEq(c1: YandexConfig, c2: YandexConfig): boolean {
  return c1.token == c2.token;
}

export interface JottacloudConfig extends FSConfigBase {
  // the account's internal name, a part of every file address
  user: string;
  email: string;
  accessToken: string;
  // changes with every refresh of the access token
  refreshToken: string;
  // when the access token ends, in ms since epoch
  tokenExpires: number;
  // where the tokens are refreshed
  tokenURL: string;
}

export function createJottacloudConfig(): JottacloudConfig {
  return {
    fsID: newFsID(fsTypeJottacloud),
    name: "",
    user: "",
    email: "",
    accessToken: "",
    refreshToken: "",
    tokenExpires: 0,
    tokenURL: "",
  };
}

export function jottacloudConfigEq(c1: JottacloudConfig, c2: JottacloudConfig): boolean {
  return c1.user == c2.user;
}

// How to log in to OpenStack Swift:
// v3: Keystone v3 with a user and password
// v3app: Keystone v3 with an application credential. user is its id, key its secret
// v1: TempAuth with a user and key
export type SwiftAuth = "v3" | "v3app" | "v1";

export interface SwiftConfig extends FSConfigBase {
  authUrl: string;
  auth: SwiftAuth;
  user: string;
  key: string;
  // v3 only. Empty means "Default".
  domain: string;
  // v3 only
  project: string;
  // empty means the first region with object storage
  region: string;
  container: string;
}

export function createSwiftConfig(): SwiftConfig {
  return {
    fsID: newFsID(fsTypeSwift),
    name: "",
    authUrl: "",
    auth: "v3",
    user: "",
    key: "",
    domain: "",
    project: "",
    region: "",
    container: "",
  };
}

export function swiftConfigEq(c1: SwiftConfig, c2: SwiftConfig): boolean {
  return (
    c1.authUrl == c2.authUrl &&
    c1.auth == c2.auth &&
    c1.user == c2.user &&
    c1.key == c2.key &&
    c1.domain == c2.domain &&
    c1.project == c2.project &&
    c1.region == c2.region &&
    c1.container == c2.container
  );
}

export interface FichierConfig extends FSConfigBase {
  apiKey: string;
}

export function createFichierConfig(): FichierConfig {
  return {
    fsID: newFsID(fsTypeFichier),
    name: "",
    apiKey: "",
  };
}

export function fichierConfigEq(c1: FichierConfig, c2: FichierConfig): boolean {
  return c1.apiKey == c2.apiKey;
}

export interface OpendriveConfig extends FSConfigBase {
  // session id from the login: the password is not kept
  sessionID: string;
  username: string;
}

export function createOpendriveConfig(): OpendriveConfig {
  return {
    fsID: newFsID(fsTypeOpendrive),
    name: "",
    sessionID: "",
    username: "",
  };
}

export function opendriveConfigEq(c1: OpendriveConfig, c2: OpendriveConfig): boolean {
  return c1.username == c2.username;
}

export interface GofileConfig extends FSConfigBase {
  // API token of the account
  token: string;
  // found with the token when connecting
  rootFolderID: string;
  email: string;
}

export function createGofileConfig(): GofileConfig {
  return {
    fsID: newFsID(fsTypeGofile),
    name: "",
    token: "",
    rootFolderID: "",
    email: "",
  };
}

export function gofileConfigEq(c1: GofileConfig, c2: GofileConfig): boolean {
  return c1.rootFolderID == c2.rootFolderID;
}

export interface PremiumizeConfig extends FSConfigBase {
  apiKey: string;
  // found with the key when connecting
  customerID: string;
}

export function createPremiumizeConfig(): PremiumizeConfig {
  return {
    fsID: newFsID(fsTypePremiumize),
    name: "",
    apiKey: "",
    customerID: "",
  };
}

export function premiumizeConfigEq(c1: PremiumizeConfig, c2: PremiumizeConfig): boolean {
  return c1.customerID == c2.customerID;
}

export interface InternetArchiveConfig extends FSConfigBase {
  accessKey: string;
  secretKey: string;
  // found with the keys when connecting; can be empty
  account: string;
}

export function createInternetArchiveConfig(): InternetArchiveConfig {
  return {
    fsID: newFsID(fsTypeInternetArchive),
    name: "",
    accessKey: "",
    secretKey: "",
    account: "",
  };
}

export function internetArchiveConfigEq(c1: InternetArchiveConfig, c2: InternetArchiveConfig): boolean {
  return c1.accessKey == c2.accessKey;
}

export interface AzureFilesConfig extends FSConfigBase {
  account: string;
  key: string;
  share: string;
}

export function createAzureFilesConfig(): AzureFilesConfig {
  return {
    fsID: newFsID(fsTypeAzureFiles),
    name: "",
    account: "",
    key: "",
    share: "",
  };
}

export function azureFilesConfigEq(c1: AzureFilesConfig, c2: AzureFilesConfig): boolean {
  return c1.account == c2.account && c1.key == c2.key && c1.share == c2.share;
}

export interface HttpConfig extends FSConfigBase {
  // address of the top directory listing page, ends with "/"
  url: string;
}

export function createHttpConfig(): HttpConfig {
  return {
    fsID: newFsID(fsTypeHttp),
    name: "",
    url: "",
  };
}

export function httpConfigEq(c1: HttpConfig, c2: HttpConfig): boolean {
  return c1.url == c2.url;
}

export interface FilesComConfig extends FSConfigBase {
  // subdomain of files.com or a custom domain, "" for the default site
  site: string;
  apiKey: string;
}

export function createFilesComConfig(): FilesComConfig {
  return {
    fsID: newFsID(fsTypeFilesCom),
    name: "",
    site: "",
    apiKey: "",
  };
}

export function filesComConfigEq(c1: FilesComConfig, c2: FilesComConfig): boolean {
  return c1.site == c2.site && c1.apiKey == c2.apiKey;
}

export interface FileluConfig extends FSConfigBase {
  apiKey: string;
}

export function createFileluConfig(): FileluConfig {
  return {
    fsID: newFsID(fsTypeFilelu),
    name: "",
    apiKey: "",
  };
}

export function fileluConfigEq(c1: FileluConfig, c2: FileluConfig): boolean {
  return c1.apiKey == c2.apiKey;
}

export interface ImagekitConfig extends FSConfigBase {
  privateKey: string;
}

export function createImagekitConfig(): ImagekitConfig {
  return {
    fsID: newFsID(fsTypeImagekit),
    name: "",
    privateKey: "",
  };
}

export function imagekitConfigEq(c1: ImagekitConfig, c2: ImagekitConfig): boolean {
  return c1.privateKey == c2.privateKey;
}

export interface CloudinaryConfig extends FSConfigBase {
  cloudName: string;
  apiKey: string;
  apiSecret: string;
}

export function createCloudinaryConfig(): CloudinaryConfig {
  return {
    fsID: newFsID(fsTypeCloudinary),
    name: "",
    cloudName: "",
    apiKey: "",
    apiSecret: "",
  };
}

export function cloudinaryConfigEq(c1: CloudinaryConfig, c2: CloudinaryConfig): boolean {
  return c1.cloudName == c2.cloudName && c1.apiKey == c2.apiKey;
}

export interface DrimeConfig extends FSConfigBase {
  accessToken: string;
}

export function createDrimeConfig(): DrimeConfig {
  return {
    fsID: newFsID(fsTypeDrime),
    name: "",
    accessToken: "",
  };
}

export function drimeConfigEq(c1: DrimeConfig, c2: DrimeConfig): boolean {
  return c1.accessToken == c2.accessToken;
}

export interface QuatrixConfig extends FSConfigBase {
  // host of the account, like mycompany.quatrix.it
  host: string;
  apiKey: string;
}

export function createQuatrixConfig(): QuatrixConfig {
  return {
    fsID: newFsID(fsTypeQuatrix),
    name: "",
    host: "",
    apiKey: "",
  };
}

export function quatrixConfigEq(c1: QuatrixConfig, c2: QuatrixConfig): boolean {
  return c1.host == c2.host && c1.apiKey == c2.apiKey;
}

export interface LinkboxConfig extends FSConfigBase {
  // the API token of the account, for uploads and other changes
  token: string;
  // what the user signs in to Linkbox with, for listing
  email: string;
  password: string;
}

export function createLinkboxConfig(): LinkboxConfig {
  return {
    fsID: newFsID(fsTypeLinkbox),
    name: "",
    token: "",
    email: "",
    password: "",
  };
}

export function linkboxConfigEq(c1: LinkboxConfig, c2: LinkboxConfig): boolean {
  return c1.email == c2.email;
}

export interface UloztoConfig extends FSConfigBase {
  // the token of an app, which Uloz.to gives out
  appToken: string;
  // what the user signs in to Uloz.to with
  username: string;
  password: string;
}

export function createUloztoConfig(): UloztoConfig {
  return {
    fsID: newFsID(fsTypeUlozto),
    name: "",
    appToken: "",
    username: "",
    password: "",
  };
}

export function uloztoConfigEq(c1: UloztoConfig, c2: UloztoConfig): boolean {
  return c1.username == c2.username;
}

// Where configs are kept between sessions. state.svelte.ts sets it. A backend
// whose tokens change while it is mounted uses it to keep the saved copy of
// its config current.
export interface FsConfigStore {
  // the saved copy of a config as it is now: another tab may have changed it
  load(c: FSConfig): FSConfig | null;
  // replaces the saved copy of a config. Does nothing when there is none.
  save(c: FSConfig): void;
}

let fsConfigStore: FsConfigStore | null = null;

export function setFsConfigStore(store: FsConfigStore | null) {
  fsConfigStore = store;
}

export function loadSavedFsConfig(c: FSConfig): FSConfig | null {
  return fsConfigStore ? fsConfigStore.load(c) : null;
}

export function saveFsConfig(c: FSConfig) {
  if (fsConfigStore) {
    fsConfigStore.save(c);
  }
}

export interface LocalConfig extends FSConfigBase {
  // Not there in a saved connection: the handle of one is kept in IndexedDB
  // under fsID, see local-handles.ts.
  dirHandle: FileSystemDirectoryHandle;
}

export function createLocalConfig(dirHandle: FileSystemDirectoryHandle, name: string): LocalConfig {
  return {
    fsID: newFsID(fsTypeLocal),
    name: name,
    dirHandle: dirHandle,
  };
}

export function localConfigEq(c1: LocalConfig, c2: LocalConfig): boolean {
  // a folder opened from a saved connection has the id of that connection
  if (c1.fsID == c2.fsID) {
    return true;
  }
  return !!c1.dirHandle && c1.dirHandle == c2.dirHandle;
}

export interface BackBlazeConfig extends FSConfigBase {
  appKeyId: string;
  appKey: string;
  bucketId: string;
  bucketName: string;
}

export function createBackBlazeConfig(): BackBlazeConfig {
  return {
    fsID: newFsID(fsTypeBackBlaze),
    name: "BackBlaze",
    appKeyId: "",
    appKey: "",
    bucketId: "",
    bucketName: "",
  };
}

export function backBlazeConfigEq(c1: BackBlazeConfig, c2: BackBlazeConfig): boolean {
  return (
    c1.appKeyId == c2.appKeyId && c1.appKey == c2.appKey && c1.bucketId == c2.bucketId && c1.bucketName == c2.bucketName
  );
}

export interface TestFilesConfig extends FSConfigBase {
  // no additional fields needed for test files
}

export function createTestFilesConfig(): TestFilesConfig {
  return {
    fsID: fsTypeTestFiles, // only one testfiles connection
    name: "Test Files",
  };
}

export function fsConfigEq(c1: FSConfig, c2: FSConfig): boolean {
  // for configs that only have one possible instance, we can just compare fsID
  if (c1.fsID === c2.fsID) {
    return true;
  }
  let fs1Type = fsTypeFromID(c1.fsID);
  let fs2Type = fsTypeFromID(c2.fsID);
  if (fs1Type !== fs2Type) {
    return false;
  }
  switch (fs1Type) {
    case fsTypeS3:
      let s3c1 = c1 as S3Config;
      let s3c2 = c2 as S3Config;
      return s3ConfigEq(s3c1, s3c2);
    case fsTypeBackBlaze:
      let bb1 = c1 as BackBlazeConfig;
      let bb2 = c2 as BackBlazeConfig;
      return backBlazeConfigEq(bb1, bb2);
    case fsTypeLocal:
      let l1 = c1 as LocalConfig;
      let l2 = c2 as LocalConfig;
      return localConfigEq(l1, l2);
    case fsTypePutio:
      return putioConfigEq(c1 as PutioConfig, c2 as PutioConfig);
    case fsTypePixeldrain:
      return pixeldrainConfigEq(c1 as PixeldrainConfig, c2 as PixeldrainConfig);
    case fsTypeStorj:
      return storjConfigEq(c1 as StorjConfig, c2 as StorjConfig);
    case fsTypeSftp:
      return sftpConfigEq(c1 as SftpConfig, c2 as SftpConfig);
    case fsTypeFtp:
      return ftpConfigEq(c1 as FtpConfig, c2 as FtpConfig);
    case fsTypeSmb:
      return smbConfigEq(c1 as SmbConfig, c2 as SmbConfig);
    case fsTypeWebdav:
      return webdavConfigEq(c1 as WebdavConfig, c2 as WebdavConfig);
    case fsTypeBox:
      return boxConfigEq(c1 as BoxConfig, c2 as BoxConfig);
    case fsTypeAzureBlob:
      return azureBlobConfigEq(c1 as AzureBlobConfig, c2 as AzureBlobConfig);
    case fsTypeGcs:
      return gcsConfigEq(c1 as GcsConfig, c2 as GcsConfig);
    case fsTypePcloud:
      return pcloudConfigEq(c1 as PcloudConfig, c2 as PcloudConfig);
    case fsTypeMega:
      return megaConfigEq(c1 as MegaConfig, c2 as MegaConfig);
    case fsTypeKoofr:
      return koofrConfigEq(c1 as KoofrConfig, c2 as KoofrConfig);
    case fsTypeSeafile:
      return seafileConfigEq(c1 as SeafileConfig, c2 as SeafileConfig);
    case fsTypeYandex:
      return yandexConfigEq(c1 as YandexConfig, c2 as YandexConfig);
    case fsTypeJottacloud:
      return jottacloudConfigEq(c1 as JottacloudConfig, c2 as JottacloudConfig);
    case fsTypeSwift:
      return swiftConfigEq(c1 as SwiftConfig, c2 as SwiftConfig);
    case fsTypeFichier:
      return fichierConfigEq(c1 as FichierConfig, c2 as FichierConfig);
    case fsTypeOpendrive:
      return opendriveConfigEq(c1 as OpendriveConfig, c2 as OpendriveConfig);
    case fsTypeGofile:
      return gofileConfigEq(c1 as GofileConfig, c2 as GofileConfig);
    case fsTypePremiumize:
      return premiumizeConfigEq(c1 as PremiumizeConfig, c2 as PremiumizeConfig);
    case fsTypeInternetArchive:
      return internetArchiveConfigEq(c1 as InternetArchiveConfig, c2 as InternetArchiveConfig);
    case fsTypeAzureFiles:
      return azureFilesConfigEq(c1 as AzureFilesConfig, c2 as AzureFilesConfig);
    case fsTypeHttp:
      return httpConfigEq(c1 as HttpConfig, c2 as HttpConfig);
    case fsTypeFilesCom:
      return filesComConfigEq(c1 as FilesComConfig, c2 as FilesComConfig);
    case fsTypeFilelu:
      return fileluConfigEq(c1 as FileluConfig, c2 as FileluConfig);
    case fsTypeImagekit:
      return imagekitConfigEq(c1 as ImagekitConfig, c2 as ImagekitConfig);
    case fsTypeCloudinary:
      return cloudinaryConfigEq(c1 as CloudinaryConfig, c2 as CloudinaryConfig);
    case fsTypeDrime:
      return drimeConfigEq(c1 as DrimeConfig, c2 as DrimeConfig);
    case fsTypeQuatrix:
      return quatrixConfigEq(c1 as QuatrixConfig, c2 as QuatrixConfig);
    case fsTypeLinkbox:
      return linkboxConfigEq(c1 as LinkboxConfig, c2 as LinkboxConfig);
    case fsTypeUlozto:
      return uloztoConfigEq(c1 as UloztoConfig, c2 as UloztoConfig);
  }
  return false;
}

export type FSConfig =
  | S3Config
  | DropboxConfig
  | OneDriveConfig
  | GDriveConfig
  | PutioConfig
  | PixeldrainConfig
  | StorjConfig
  | SftpConfig
  | FtpConfig
  | SmbConfig
  | WebdavConfig
  | BoxConfig
  | AzureBlobConfig
  | GcsConfig
  | PcloudConfig
  | MegaConfig
  | KoofrConfig
  | SeafileConfig
  | YandexConfig
  | JottacloudConfig
  | SwiftConfig
  | FichierConfig
  | OpendriveConfig
  | GofileConfig
  | PremiumizeConfig
  | InternetArchiveConfig
  | AzureFilesConfig
  | HttpConfig
  | FilesComConfig
  | FileluConfig
  | ImagekitConfig
  | CloudinaryConfig
  | DrimeConfig
  | QuatrixConfig
  | LinkboxConfig
  | UloztoConfig
  | LocalConfig
  | BackBlazeConfig
  | TestFilesConfig;

// re-using this in mulitple contexts for simplicity
export class FileInfo {
  name = "";
  size = 0;
  file: File | undefined;
  path = "";
  // when uploading, this is destination path
  dstPath = "";
}

/**
 * Copy properties from objet to FSEntry
 */
export function fsCopyProps(props: string[], o: object, e: any[]): void {
  for (let propName of props) {
    let v = getObjectValue(o, propName);
    if (v) {
      e.push(propName, v);
    }
  }
}

export class DirAndFile {
  dir: FsDir;
  entry: FsEntry = kInvalidEntry;
  constructor(dir: FsDir) {
    this.dir = dir;
  }
}

export function fsFullName(fs: FS): string {
  let conf = fs.config;
  let name = conf.name;
  let fsType = fsTypeFromFSConfig(conf);
  switch (fsType) {
    case fsTypeS3:
      //let s3Config: S3Config = conf;
      name = "s3 (" + name + ")";
      // s3Config.bucket + "." + s3Config.endpoint;
      break;
    case fsTypeBackBlaze:
      //let bbConfig: BackBlazeConfig = conf;
      name = "BackBlaze (" + name + ")";
      break;
    case fsTypeLocal:
      name = "Local Folder (" + name + ")";
      break;
    case fsTypeDropbox:
      name = "DropBox (" + name + ")";
      break;
    case fsTypeOneDrive:
      name = "OneDrive (" + name + ")";
      break;
    case fsTypeGDrive:
      if (name == "") {
        name = (conf as GDriveConfig).email;
      }
      name = "Google Drive (" + name + ")";
      break;
    case fsTypePutio:
      name = "Put.io (" + name + ")";
      break;
    case fsTypePixeldrain:
      name = "Pixeldrain (" + name + ")";
      break;
    case fsTypeStorj:
      if (name == "") {
        name = (conf as StorjConfig).bucket;
      }
      name = "Storj (" + name + ")";
      break;
    case fsTypeSftp:
      if (name == "") {
        name = (conf as SftpConfig).user + "@" + (conf as SftpConfig).host;
      }
      name = "SFTP (" + name + ")";
      break;
    case fsTypeFtp:
      if (name == "") {
        name = (conf as FtpConfig).host;
      }
      name = "FTP (" + name + ")";
      break;
    case fsTypeSmb:
      if (name == "") {
        name = (conf as SmbConfig).host;
      }
      name = "SMB (" + name + ")";
      break;
    case fsTypeWebdav:
      if (name == "") {
        name = (conf as WebdavConfig).url;
      }
      name = "WebDAV (" + name + ")";
      break;
    case fsTypeBox:
      if (name == "") {
        name = (conf as BoxConfig).login;
      }
      name = "Box (" + name + ")";
      break;
    case fsTypeAzureBlob:
      if (name == "") {
        name = (conf as AzureBlobConfig).container;
      }
      name = "Azure Blob (" + name + ")";
      break;
    case fsTypeGcs:
      if (name == "") {
        name = (conf as GcsConfig).bucket;
      }
      name = "Google Cloud Storage (" + name + ")";
      break;
    case fsTypePcloud:
      if (name == "") {
        name = (conf as PcloudConfig).email;
      }
      name = "pCloud (" + name + ")";
      break;
    case fsTypeMega:
      if (name == "") {
        name = (conf as MegaConfig).email;
      }
      name = "Mega (" + name + ")";
      break;
    case fsTypeKoofr:
      if (name == "") {
        name = (conf as KoofrConfig).email;
      }
      name = "Koofr (" + name + ")";
      break;
    case fsTypeSeafile:
      if (name == "") {
        name = (conf as SeafileConfig).url;
      }
      name = "Seafile (" + name + ")";
      break;
    case fsTypeYandex:
      if (name == "") {
        name = (conf as YandexConfig).login;
      }
      name = "Yandex Disk (" + name + ")";
      break;
    case fsTypeJottacloud:
      if (name == "") {
        name = (conf as JottacloudConfig).email;
      }
      name = "Jottacloud (" + name + ")";
      break;
    case fsTypeSwift:
      if (name == "") {
        name = (conf as SwiftConfig).container;
      }
      name = "Swift (" + name + ")";
      break;
    case fsTypeFichier:
      name = "1Fichier (" + name + ")";
      break;
    case fsTypeOpendrive:
      if (name == "") {
        name = (conf as OpendriveConfig).username;
      }
      name = "OpenDrive (" + name + ")";
      break;
    case fsTypeGofile:
      if (name == "") {
        name = (conf as GofileConfig).email;
      }
      name = "Gofile (" + name + ")";
      break;
    case fsTypePremiumize:
      if (name == "") {
        name = (conf as PremiumizeConfig).customerID;
      }
      name = "premiumize.me (" + name + ")";
      break;
    case fsTypeInternetArchive:
      if (name == "") {
        name = (conf as InternetArchiveConfig).account;
      }
      name = name == "" ? "Internet Archive" : "Internet Archive (" + name + ")";
      break;
    case fsTypeAzureFiles:
      if (name == "") {
        name = (conf as AzureFilesConfig).share;
      }
      name = "Azure Files (" + name + ")";
      break;
    case fsTypeHttp:
      if (name == "") {
        name = (conf as HttpConfig).url;
      }
      name = "HTTP (" + name + ")";
      break;
    case fsTypeFilesCom:
      if (name == "") {
        name = (conf as FilesComConfig).site;
      }
      name = "Files.com (" + name + ")";
      break;
    case fsTypeFilelu:
      name = "FileLu (" + name + ")";
      break;
    case fsTypeImagekit:
      name = "ImageKit (" + name + ")";
      break;
    case fsTypeCloudinary:
      name = "Cloudinary (" + name + ")";
      break;
    case fsTypeDrime:
      name = "Drime (" + name + ")";
      break;
    case fsTypeQuatrix:
      name = "Quatrix (" + name + ")";
      break;
    case fsTypeLinkbox:
      name = "Linkbox (" + name + ")";
      break;
    case fsTypeUlozto:
      name = "Uloz.to (" + name + ")";
      break;
  }
  return name;
}

export function fsConfigFullName(fsConfig: FSConfig): string {
  let name = fsConfig.name;
  let fsType = fsTypeFromFSConfig(fsConfig);
  switch (fsType) {
    case fsTypeS3:
      let s3Config = fsConfig as S3Config;
      if (name == "") {
        name = "S3 (" + s3Config.bucket + ")";
      }
      break;

    case fsTypeBackBlaze:
      let bbConfig = fsConfig as BackBlazeConfig;
      name = "BackBlaze (" + bbConfig.bucketName + ")";
      break;

    case fsTypeLocal:
      name = "Local Folder (" + name + ")";
      break;

    case fsTypeGDrive:
      if (name == "") {
        name = (fsConfig as GDriveConfig).email;
      }
      name = "Google Drive (" + name + ")";
      break;

    case fsTypePutio:
      name = "Put.io (" + name + ")";
      break;

    case fsTypePixeldrain:
      name = "Pixeldrain (" + name + ")";
      break;

    case fsTypeStorj:
      if (name == "") {
        name = (fsConfig as StorjConfig).bucket;
      }
      name = "Storj (" + name + ")";
      break;

    case fsTypeSftp:
      if (name == "") {
        let sftpConfig = fsConfig as SftpConfig;
        name = sftpConfig.user + "@" + sftpConfig.host;
      }
      name = "SFTP (" + name + ")";
      break;

    case fsTypeFtp:
      if (name == "") {
        name = (fsConfig as FtpConfig).host;
      }
      name = "FTP (" + name + ")";
      break;

    case fsTypeSmb:
      if (name == "") {
        name = (fsConfig as SmbConfig).host;
      }
      name = "SMB (" + name + ")";
      break;

    case fsTypeWebdav:
      if (name == "") {
        name = (fsConfig as WebdavConfig).url;
      }
      name = "WebDAV (" + name + ")";
      break;

    case fsTypeBox:
      if (name == "") {
        name = (fsConfig as BoxConfig).login;
      }
      name = "Box (" + name + ")";
      break;

    case fsTypeAzureBlob:
      if (name == "") {
        name = (fsConfig as AzureBlobConfig).container;
      }
      name = "Azure Blob (" + name + ")";
      break;

    case fsTypeGcs:
      if (name == "") {
        name = (fsConfig as GcsConfig).bucket;
      }
      name = "Google Cloud Storage (" + name + ")";
      break;

    case fsTypePcloud:
      if (name == "") {
        name = (fsConfig as PcloudConfig).email;
      }
      name = "pCloud (" + name + ")";
      break;

    case fsTypeMega:
      if (name == "") {
        name = (fsConfig as MegaConfig).email;
      }
      name = "Mega (" + name + ")";
      break;

    case fsTypeKoofr:
      if (name == "") {
        name = (fsConfig as KoofrConfig).email;
      }
      name = "Koofr (" + name + ")";
      break;

    case fsTypeSeafile:
      if (name == "") {
        name = (fsConfig as SeafileConfig).url;
      }
      name = "Seafile (" + name + ")";
      break;

    case fsTypeYandex:
      if (name == "") {
        name = (fsConfig as YandexConfig).login;
      }
      name = "Yandex Disk (" + name + ")";
      break;

    case fsTypeJottacloud:
      if (name == "") {
        name = (fsConfig as JottacloudConfig).email;
      }
      name = "Jottacloud (" + name + ")";
      break;

    case fsTypeSwift:
      if (name == "") {
        name = (fsConfig as SwiftConfig).container;
      }
      name = "Swift (" + name + ")";
      break;

    case fsTypeFichier:
      name = "1Fichier (" + name + ")";
      break;

    case fsTypeOpendrive:
      if (name == "") {
        name = (fsConfig as OpendriveConfig).username;
      }
      name = "OpenDrive (" + name + ")";
      break;
    case fsTypeGofile:
      if (name == "") {
        name = (fsConfig as GofileConfig).email;
      }
      name = "Gofile (" + name + ")";
      break;
    case fsTypePremiumize:
      if (name == "") {
        name = (fsConfig as PremiumizeConfig).customerID;
      }
      name = "premiumize.me (" + name + ")";
      break;
    case fsTypeInternetArchive:
      if (name == "") {
        name = (fsConfig as InternetArchiveConfig).account;
      }
      name = name == "" ? "Internet Archive" : "Internet Archive (" + name + ")";
      break;
    case fsTypeAzureFiles:
      if (name == "") {
        name = (fsConfig as AzureFilesConfig).share;
      }
      name = "Azure Files (" + name + ")";
      break;
    case fsTypeHttp:
      if (name == "") {
        name = (fsConfig as HttpConfig).url;
      }
      name = "HTTP (" + name + ")";
      break;
    case fsTypeFilesCom:
      if (name == "") {
        name = (fsConfig as FilesComConfig).site;
      }
      name = "Files.com (" + name + ")";
      break;
    case fsTypeFilelu:
      name = "FileLu (" + name + ")";
      break;
    case fsTypeImagekit:
      name = "ImageKit (" + name + ")";
      break;
    case fsTypeCloudinary:
      name = "Cloudinary (" + name + ")";
      break;
    case fsTypeDrime:
      name = "Drime (" + name + ")";
      break;
    case fsTypeQuatrix:
      name = "Quatrix (" + name + ")";
      break;
    case fsTypeLinkbox:
      name = "Linkbox (" + name + ")";
      break;
    case fsTypeUlozto:
      name = "Uloz.to (" + name + ")";
      break;
  }
  return name;
}

// A directory with the file system it is on, as shown to the user,
// e.g. "pixeldrain:/docs".
export function fsFullDir(fs: FS, dir: string): string {
  let fsType = fs.type;
  let s = fs.name;
  // a type ends with ":"
  if (fsType == fsTypeS3) {
    s = fsType + (fs.config as S3Config).bucket;
  } else if (
    fsType == fsTypeBackBlaze ||
    fsType == fsTypeDropbox ||
    fsType == fsTypeOneDrive ||
    fsType == fsTypeTestFiles
  ) {
    s = fsType.slice(0, -1);
  }
  return s + ":" + dir;
}

export class FS {
  config: FSConfig;
  dirs: Map<string, FsDir> = new Map();
  // sizes and dates of directories found by scans, see dir-stats.ts
  dirStats = new DirStatsCache();
  filesCount = 0;
  // rename() works on directories. False for object stores like S3, where a
  // directory is only a key prefix and renaming it means copying every object.
  supportsDirRename = false;

  constructor(config: FSConfig) {
    this.config = config;
  }

  get name(): string {
    return this.config.name;
  }

  get type(): FSType {
    return fsTypeFromFSConfig(this.config);
  }

  findDirAndFileFSEntry(path: string): DirAndFile | null {
    const isDir = path.endsWith("/");
    if (isDir && path != "/") {
      path = path.substring(0, len(path) - 1);
    }

    const parts = filepath.split(path);
    let dirName = parts.dir;
    let entryName = parts.name;

    const dir = this.dirs.get(dirName);
    if (!dir) {
      return null;
    }
    let res = new DirAndFile(dir);
    if (entryName !== "") {
      let e = dir.findChild(entryName);
      if (e !== kInvalidEntry) {
        res.entry = e;
        return res;
      }
    }
    return res;
  }

  calcDirLastMod(dir: FsDir): number {
    // the api doesn't provide lastMod time for directories so we
    // calculate it as the lastMod of newest file in a directory
    let lastMod = 0;
    let n = dir.count();
    for (let i = 0; i < n; i++) {
      if (dir.entryIsDir(i)) {
        continue;
      }
      let m = dir.entryModTime(i);
      if (m > lastMod) {
        lastMod = m;
      }
    }
    if (lastMod == 0) {
      lastMod = Date.now();
    }
    return lastMod;
  }

  cacheDir(dir: FsDir): void {
    let dirPath = dir.path;
    throwIf(!dirPath.startsWith("/"), `${dirPath} doesn't start with '/'`);
    console.log(`FS.cacheDir for dir: '${dirPath}'`);

    this.filesCount += dir.fileCount;
    let old = this.dirs.get(dirPath);
    if (old) {
      this.filesCount -= old.fileCount;
    }
    this.dirs.set(dirPath, dir);
  }

  removeCachedDir(dirPath: string): void {
    console.log(`FS.removeCachedDir: ${dirPath}`);
    let e = this.dirs.get(dirPath);
    if (!e) {
      return;
    }
    this.filesCount -= e.fileCount;
    this.dirs.delete(dirPath);
  }

  removeCachedDirRecur(dirPath: string): void {
    this.removeCachedDir(dirPath);
    // remove all sub-directories of dirPath
    let prefix = dirPath + "/";
    let dirsToRemove = [];
    for (let dir of this.dirs.keys()) {
      if (dir.startsWith(prefix)) {
        dirsToRemove.push(dir);
      }
    }
    for (let dir of dirsToRemove) {
      this.removeCachedDir(dir);
    }
  }

  findEntries(path: string): { dir: FsDir; entries: FsEntry[] } {
    let dn = filepath.split(path);
    let dir = dn.dir;
    if (dir == "") {
      dir = "/";
    }
    const de = this.dirs.get(dir);
    if (!de) {
      throw new Error(`Didn't find ${dir} parent dir for path ${path} `);
    }
    if (path == "/") {
      return { dir: de, entries: [] };
    }

    let entries: FsEntry[] = [];
    let n = de.count();
    for (let e = 0; e < n; e++) {
      if (de.entryName(e) == dn.name) {
        entries.push(e);
      }
    }
    if (len(entries) == 0) {
      throw new Error(`Didn't find entry ${dn.name} in dir ${dn.dir} `);
    }
    return { dir: de, entries };
  }

  findFirstEntryOfType(path: string, isDir: boolean): { dir: FsDir; entry: FsEntry } {
    let found = this.findEntries(path);
    for (let e of found.entries) {
      if (found.dir.entryIsDir(e) == isDir) {
        return { dir: found.dir, entry: e };
      }
    }
    throw new Error(`Didn't find enry for dir ${path}`);
  }

  skipReadParentDirs = false;
  async readParentDirs(dirPath: string, force: boolean, progress: Writable<any>): Promise<void> {
    if (this.skipReadParentDirs) {
      return;
    }
    this.skipReadParentDirs = true;
    let dirs: string[] = [];
    dirPath = filepath.dir(dirPath);
    while (dirPath != "/") {
      dirs.push(dirPath);
      dirPath = filepath.dir(dirPath);
    }
    dirs.push("/");

    let n = len(dirs);
    for (let i = 0; i < n; i++) {
      let dir = dirs[n - i - 1];
      await this.readDir(dir, false, progress);
    }
    this.skipReadParentDirs = false;
  }

  /**
   * read fs entries in a given directory
   */
  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throw new Error(`readDir() not implemented for ${this.type}`);
  }

  /**
   * Cache lookup, progress text, and store. `load` runs only on a miss.
   */
  protected async loadDir(
    dirPath: string,
    force: boolean,
    progress: Writable<any>,
    load: () => Promise<FsDir>,
  ): Promise<FsDir> {
    progress.set(`reading dir ${dirPath}, ${this.dirs.size} dirs, ${this.filesCount} files`);
    if (!force) {
      const cached = this.dirs.get(dirPath);
      if (cached) {
        console.log(`${this.type} readDir: cache hit ${dirPath}`);
        return cached;
      }
    }
    const dur = startTimer();
    let dir = await load();
    throwIf(dir.path != dirPath, `loaded ${dir.path}, expected ${dirPath}`);
    console.log(`${this.type} readDir: ${dir.count()} entries for ${dirPath} in ${dur()} ms`);
    this.cacheDir(dir);
    return dir;
  }

  /**
   * get publicly visible URL
   */
  async getPublicURL(path: string): Promise<string> {
    throw new Error(`getPublicURL() not implemented for${this.type}`);
  }

  /**
   * Generic download function that works using getPublicURL
   * Some filesystems might implement optimized version
   */
  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let uri = await this.getPublicURL(path);
    let blob = await xhrGetAsBlob(uri, httpProgress);
    return blob;
  }

  downloadName(path: string): string {
    return filepath.base(path);
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    throw new Error(`uploadFile() not implemented for ${this.type}`);
  }

  /**
   * upload files to this filesystem
   */
  async uploadFiles(files: FileInfo[], progress: Writable<any>): Promise<void> {
    console.log("local.uploadFiles() files:", files);
    for (const fi of files) {
      if (!fi.file) {
        throw new Error(`uploadFiles: file is undefined for ${fi.path}`);
      }
      let msg = `uploading ${fi.path}`;
      progress.set(msg);
      console.log("uploadFiles:", msg);

      function onProgress(size: number, totalSize: number, durMs: number) {
        console.log("onProgress: size:", size, "totalSize:", totalSize, "durMs:", durMs);
        msg = `uploading ${fi.path} ${fmtSize(size)}`;
        if (totalSize > size) {
          msg += ` of ${fmtSize(totalSize)}`;
        }
        msg += ` in ${durMs} ms`;
        progress.set(msg);
      }
      progress.set(msg);

      await this.uploadFile(fi.dstPath, fi.file, onProgress);
    }
  }

  /**
   * rename a file
   */
  async rename(path: string, newPath: string): Promise<void> {
    throw new Error(`rename() not implemented for ${this.type}`);
  }

  /**
   * create a new directory
   */
  async createDirectory(dirPath: string): Promise<void> {
    throw new Error(`createDirectory() not implemented for ${this.type}`);
  }

  async deleteFile(path: string): Promise<void> {
    throw new Error(`deleteFile() not implemented for ${this.type}`);
  }

  /**
   * delete files and directories in this filesystem
   */
  async deleteFiles(paths: string[], progress: Writable<any>): Promise<void> {
    for (let path of paths) {
      progress.set(`deleting ${path}`);
      await this.deleteFile(path);
    }
  }
}

/**
 * get paths for files in a directory, relative to directory name
 * recursively if needed
 */
export async function getDirFilesRelativePath(fs: FS, dir: string, recursive: boolean): Promise<string[]> {
  let toRead = [dir];
  let res = [];
  let dummyProgress = writable("");
  let relativeStarts = len(dir);

  while (len(toRead) > 0) {
    let dirPath = toRead.shift() as string;
    let dir = await fs.readDir(dirPath, true, dummyProgress);
    let n = dir.count();
    for (let e = 0; e < n; e++) {
      let path = filepath.join(dirPath, dir.entryName(e));
      if (!dir.entryIsDir(e)) {
        path = path.substring(relativeStarts);
        path = trimSuffix(path, "/");
        res.push(path);
      } else if (recursive) {
        toRead.push(path);
      }
    }
  }

  return res;
}

export class FSDirState {
  fs: FS;
  currDir = "";
  // if we're reading directory in the background,
  // this is the directory we're reading
  dirInProgress = "";
  isValidDir = true;

  onReloadCurrentDir?: () => void;

  triggerReloadCurrentDir(): void {
    if (this.onReloadCurrentDir) {
      this.onReloadCurrentDir();
    }
  }

  constructor(fs: FS, dir: string) {
    this.fs = fs;
    this.currDir = dir;
  }
}
